import {
  AfterViewInit,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  DesktopConfiguratorAction,
  DesktopConfiguratorOutlet,
  DesktopConfiguratorStep,
  DesktopConfiguratorValue,
} from './desktop-configurator-template.models';

@Component({
  selector: 'ng-casto-desktop-configurator-template',
  templateUrl: './desktop-configurator-template.component.html',
  styleUrls: ['./desktop-configurator-template.component.scss'],
})
export class DesktopConfiguratorTemplateComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() steps: DesktopConfiguratorStep[] = [];
  @Input() currentStepId: string | null = null;
  @Input() preview: DesktopConfiguratorOutlet | null = null;
  @Input() actions: DesktopConfiguratorAction[] = [];
  @Input() panelCollapsed: boolean = false;
  @Output() stepSelected = new EventEmitter<string>();
  @Output() panelCollapsedChange = new EventEmitter<boolean>();

  @ViewChild('stepHost', { read: ViewContainerRef }) private stepHost!: ViewContainerRef;
  @ViewChild('previewHost', { read: ViewContainerRef }) private previewHost!: ViewContainerRef;

  private stepComponentRef: ComponentRef<unknown> | null = null;
  private previewComponentRef: ComponentRef<unknown> | null = null;
  private stepSubscriptions: Subscription[] = [];
  private previewSubscriptions: Subscription[] = [];
  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderActiveStep();
    this.renderPreview();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;

    if (changes['steps'] || changes['currentStepId']) {
      this.renderActiveStep();
    }

    if (changes['preview']) {
      this.renderPreview();
    }
  }

  ngOnDestroy(): void {
    this.destroyStepOutlet();
    this.destroyPreviewOutlet();
  }

  protected get activeStep(): DesktopConfiguratorStep | null {
    return this.steps.find(step => step.id === this.currentStepId) ?? null;
  }

  protected canSelectStep(step: DesktopConfiguratorStep): boolean {
    return !this.resolveValue(step.disabled);
  }

  protected isStepCompleted(step: DesktopConfiguratorStep): boolean {
    return !!this.resolveValue(step.completed);
  }

  protected selectStep(step: DesktopConfiguratorStep): void {
    if (!this.canSelectStep(step)) return;
    this.stepSelected.emit(step.id);
  }

  protected togglePanel(): void {
    this.panelCollapsedChange.emit(!this.panelCollapsed);
  }

  protected isActionDisabled(action: DesktopConfiguratorAction): boolean {
    return !!this.resolveValue(action.disabled) || !!this.resolveValue(action.loading);
  }

  protected isActionLoading(action: DesktopConfiguratorAction): boolean {
    return !!this.resolveValue(action.loading);
  }

  protected getActionLabel(action: DesktopConfiguratorAction): string {
    return this.resolveValue(action.label) ?? '';
  }

  protected async runAction(action: DesktopConfiguratorAction): Promise<void> {
    if (this.isActionDisabled(action)) return;
    await action.action();
  }

  private renderActiveStep(): void {
    this.destroyStepOutlet();
    const step = this.activeStep;
    if (!step || !this.stepHost) return;

    this.stepComponentRef = this.renderOutlet(this.stepHost, step, this.stepSubscriptions);
  }

  private renderPreview(): void {
    this.destroyPreviewOutlet();
    if (!this.preview || !this.previewHost) return;

    this.previewComponentRef = this.renderOutlet(this.previewHost, this.preview, this.previewSubscriptions);
  }

  private renderOutlet(
    host: ViewContainerRef,
    outlet: DesktopConfiguratorOutlet,
    subscriptions: Subscription[]
  ): ComponentRef<unknown> {
    host.clear();
    const componentRef = host.createComponent(outlet.component);

    Object.entries(outlet.inputs ?? {}).forEach(([key, value]) => {
      componentRef.setInput(key, value);
    });

    Object.entries(outlet.outputs ?? {}).forEach(([key, handler]) => {
      const output = (componentRef.instance as Record<string, unknown>)[key];
      if (output && typeof (output as { subscribe?: unknown }).subscribe === 'function') {
        subscriptions.push((output as EventEmitter<unknown>).subscribe(value => handler(value)));
      }
    });

    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  private destroyStepOutlet(): void {
    this.stepSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.stepSubscriptions = [];
    this.stepComponentRef?.destroy();
    this.stepComponentRef = null;
    this.stepHost?.clear();
  }

  private destroyPreviewOutlet(): void {
    this.previewSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.previewSubscriptions = [];
    this.previewComponentRef?.destroy();
    this.previewComponentRef = null;
    this.previewHost?.clear();
  }

  private resolveValue<T>(value: DesktopConfiguratorValue<T> | undefined): T | undefined {
    return typeof value === 'function' ? (value as () => T)() : value;
  }
}
